import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { alpha } from "@mui/material/styles";

/**
 * Styled header panel for admin pages.
 * Gradient background, icon container, title/description, and optional actions.
 *
 * @param {object} props
 * @param {string} props.title - Page title (e.g. "Organizations")
 * @param {string} props.description - Subtitle/description text
 * @param {React.ReactNode} props.icon - MUI icon component (e.g. <BusinessIcon />)
 * @param {string} [props.backTo] - Optional route for Back button
 * @param {string} [props.backLabel] - Optional label for Back button
 * @param {React.ReactNode} [props.children] - Optional action buttons to render on the right
 */
export default function AdminPageHeader({ title, description, icon, backTo, backLabel, children }) {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        mb: 4,
        p: 2.5,
        flexWrap: "wrap",
        borderRadius: 2,
        background: (theme) => {
          const primary = theme.palette.primary.main;
          const secondary = theme.palette.secondary?.main || primary;
          return theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${alpha(primary, 0.12)} 0%, ${alpha(secondary, 0.06)} 100%)`
            : `linear-gradient(135deg, ${alpha(primary, 0.05)} 0%, ${alpha(secondary, 0.04)} 100%)`;
        },
        border: (theme) =>
          `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.1 : 0.06)}`,
      }}
    >
      {backTo && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(backTo)}
          sx={{ mr: 1 }}
        >
          {backLabel || "Back"}
        </Button>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 64,
          height: 64,
          borderRadius: 2,
          background: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.15 : 0.06),
          flexShrink: 0,
          "& .MuiSvgIcon-root": { fontSize: 36 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}
